import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faArrowLeft, faClose } from '@fortawesome/free-solid-svg-icons';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { debounce } from 'lodash';

import { getProductList, getProductDetails } from '../Controller/ProductController';
import { getStoreList } from '../Controller/StoreController';
import { createStockTransfer, getStockTransferDetails, updateStockTransfer } from '../Controller/StockTransferController';
import LoginUtility from "../../../../GlobalModules/Utility/Utility";
import { connect } from 'react-redux';

const defaultValues = {
  fromStore: null,
  toStore: null,
  notes: '',
  productSearch: null,
  // keep a single blank row for manual entry; the top search will now REUSE it
  selectedProducts: [
    { value: null, label: null, quantity: '', quantityLeft: null },
  ],
};

// --- Layout constants ---
const FIELD_HEIGHT = 38;      // height of inputs/selects
const ERROR_HEIGHT = 20;      // reserved space under each field for error
const GAP_Y = 6;              // small inner vertical gap between input and error

// Wrapper that reserves space under the field and overlays the error there
const FieldWithUnderError = ({ children, error }) => (
  <div
    style={{
      position: 'relative',
      paddingBottom: ERROR_HEIGHT + GAP_Y,
    }}
  >
    {children}
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: ERROR_HEIGHT,
        lineHeight: `${ERROR_HEIGHT}px`,
        color: '#dc3545',
        fontSize: '0.875rem',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        visibility: error ? 'visible' : 'hidden',
      }}
      title={error || ''}
    >
      {error || 'placeholder'}
    </div>
  </div>
);

// react-select inline styles with invalid + disabled state
const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: FIELD_HEIGHT,
    height: FIELD_HEIGHT,
    borderColor: hasError ? '#dc3545' : base.borderColor,
    boxShadow: hasError
      ? '0 0 0 .25rem rgba(220,53,69,.25)'
      : state.isFocused
        ? '0 0 0 .25rem rgba(13,110,253,.25)'
        : base.boxShadow,
    backgroundColor: state.isDisabled ? '#e9ecef' : base.backgroundColor,
    cursor: state.isDisabled ? 'not-allowed' : base.cursor,
    '&:hover': {
      borderColor: hasError
        ? '#dc3545'
        : (state.isFocused ? '#86b7fe' : base['&:hover']?.borderColor || base.borderColor),
    },
  }),
  singleValue: (base, state) => ({
    ...base,
    color: state.isDisabled ? '#6c757d' : base.color,
  }),
  indicatorsContainer: (base) => ({ ...base, height: FIELD_HEIGHT }),
  valueContainer: (base) => ({ ...base, height: FIELD_HEIGHT }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

const StockTransferForm = ({ isEdit = false, currentStore }) => {
  const history = useHistory();
  const { slack } = useParams();
  const [requisitionReference, setRequisitionReference] = useState('');
  const [requisitionSlack, setRequisitionSlack] = useState('');
  const [requisitionId, setRequisitionId] = useState('');

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues, mode: 'onChange' });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'selectedProducts',
    keyName: 'key',
  });

  // 👀 Live watch of the array values (fixes stale `fields` snapshots)
  const watchedRows = useWatch({ control, name: 'selectedProducts' });

  const fetchStockTransfer = async () => {
    try {
      const response = await getStockTransferDetails(slack);
      if (response.success && response.data) {
        const stockTransfer = response.data;
        reset({
          fromStore: {
            label: stockTransfer.StockTransfer_FromStore.name,
            value: stockTransfer.StockTransfer_FromStore.id,
            store_code: stockTransfer.StockTransfer_FromStore.store_code,
          },
          toStore: {
            label: stockTransfer.StockTransfer_ToStore.name,
            value: stockTransfer.StockTransfer_ToStore.id,
            store_code: stockTransfer.StockTransfer_ToStore.store_code,
          },
          notes: stockTransfer.notes,
          selectedProducts: stockTransfer.transfer_products.map((el) => ({
            value: el.product_slack,
            label: el.product_name,
            quantity: el.quantity ?? '',
            quantityLeft: el.Product.quantity,
            unitOfMeasure: el.Product?.unit_of_measure || '',
            meta_data: el.meta_data || [],
          })),
          productSearch: null,
        });
      } else {
        toast.error('Stock transfer not found');
      }
    } catch (error) {
      console.error('Error fetching stock transfer:', error);
      toast.error('Failed to fetch stock transfer details');
    }
  };

  const fetchProductOptions = async (inputValue) => {
    try {
      const response = await getProductList({ limit: 10, offset: 0, search: inputValue, store_id: localStorage.getItem('store_id'), is_ingredient:1, own_store: localStorage.getItem('store_id'), });
      if (response.success && response.data) {
        // return response.data.map((product) => ({
        //   value: product.slack,
        //   label: product.name,
        //   unitPrice: product.purchase_amount_excluding_tax,
        //   tax: product.TaxCode?.total_tax_percentage,
        //   quantityLeft: product.quantity,
        // }));
      
        return response.data.map((product) => {
          const mfgDate = getFromMetaData(product.meta_data, 'mfg_date');
          const expDate = getFromMetaData(product.meta_data, 'exp_date');
          
          // Check if store is not restaurant to show full details
          const showFullDetails = LoginUtility.isNotRestaurantStore(currentStore);
          // console.log("showFullDetails====",showFullDetails)
          // console.log("currentStore====",currentStore)
          // Create label with conditional details
          let labelParts = [product.name];
          
          if (product.product_code) {
            labelParts.push(`Code: ${product.product_code}`);
          }
          
          // Only show additional details for non-restaurant stores
          // if (showFullDetails) {
            if (product.quantity !== undefined && product.quantity !== null) {
              labelParts.push(`Qty: ${product.quantity}${product.unit_of_measure ? ` ${product.unit_of_measure}` : ''}`);
            }
            
            /*if (mfgDate) {
              labelParts.push(`Mfg: ${mfgDate}`);
            }
            
            if (expDate) {
              labelParts.push(`Exp: ${expDate}`);
            }*/
          // }
          
          return {
            value: product.slack,
            label: labelParts.join(' | '),
            unitPrice: product.purchase_amount_excluding_tax,
            tax: product.TaxCode?.total_tax_percentage && product.TaxCode?.total_tax_percentage != "" ? product.TaxCode?.total_tax_percentage : "",
            quantityLeft: product.quantity,
            unitOfMeasure: product.unit_of_measure || '',
            meta_data: product.meta_data
          };
        });
      }
      return [];
    } catch (e) {
      console.error('Error loading products:', e);
      return [];
    }
  };

  const debouncedFetchProductOptions = useMemo(
    () => debounce((inputValue, callback) => { fetchProductOptions(inputValue).then(callback); }, 400),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const fetchStoreOptions = async (inputValue) => {
    try {
      const response = await getStoreList({ limit: 10, offset: 0, search: inputValue,all:true});
      if (response.success && response.data) {
        // return response.data.map((store) => ({
        //   value: store.id,
        //   label: store.name,
        //   store_code: store.store_code,
        // }));
        return response.data
        .filter((store) => store.id !== Number(localStorage.getItem('store_id'))) // exclude current store
        .map((store) => ({
          value: store.id,
          label: store.name,
          store_code: store.store_code,
        }));
      }
      return [];
    } catch (e) {
      console.error('Error loading stores:', e);
      return [];
    }
  };

  const debouncedFetchStoreOptions = useMemo(
    () => debounce((inputValue, callback) => { fetchStoreOptions(inputValue).then(callback); }, 400),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const fetchCurrentStore = useCallback(async () => {
    try {
      const response = await getStoreList();
      if (response.success && response.data) {
        const currentStoreId = localStorage.getItem('store_id');
        // console.log("the current store",currentStoreId)
        let currentStore = response.data.find((s) => s.id === +currentStoreId);
        if (!currentStore) {
          for (const store of response.data) {
            if (store.child_stores && store.child_stores.length > 0) {
              currentStore = store.child_stores.find((cs) => cs.id === +currentStoreId);
              if (currentStore) break;
            }
          }
        }
        if (currentStore) {
          setValue('fromStore', {
            label: currentStore.name,
            value: currentStore.id,
            store_code: currentStore.store_code,
          }, { shouldValidate: true });
        } else {
          toast.error('Failed to find current store');
        }
      } else {
        toast.error('Failed to fetch current store');
      }
    } catch (e) {
      console.error('Error fetching from store:', e);
      toast.error('An error occurred while fetching current store');
    }
  }, [setValue]);

  useEffect(() => { fetchCurrentStore(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProductPickedFromTop = async (option) => {
    console.log("option===",option)
    // cleared the picker
    if (!option) {
      setValue('productSearch', null, { shouldValidate: true });
      return;
    }

    const rows = getValues('selectedProducts') || [];

    // if product already exists: increment its quantity
    const existingIdx = rows.findIndex((p) => p?.value === option.value);
    if (existingIdx !== -1) {
      const existing = rows[existingIdx] || {};
      const prevQ =
        existing.quantity === '' || existing.quantity == null
          ? 0
          : typeof existing.quantity === 'number'
            ? existing.quantity
            : parseFloat(existing.quantity);

      const nextQ = Number.isFinite(prevQ) ? prevQ + 1 : 1;

      update(existingIdx, { ...existing, quantity: nextQ });
      clearErrors(`selectedProducts.${existingIdx}.quantity`);
      setValue('productSearch', null, { shouldValidate: true });
      return;
    }

    // Prepare product data with meta information (already available in option)
    const productData = {
      ...option,
      quantity: 1,
    };

    console.log('Using meta_data from option:', option.meta_data);

    // find first truly empty row (no product chosen)
    const emptyIdx = rows.findIndex((r) => !r?.value);

    if (emptyIdx !== -1) {
      update(emptyIdx, {
        ...rows[emptyIdx],
        value: option.value,
        label: option.label,
        quantityLeft: option.quantityLeft ?? null,
        unitOfMeasure: option.unitOfMeasure ?? '',
        quantity:
            rows[emptyIdx].quantity === '' || rows[emptyIdx].quantity == null
                ? 1
                : rows[emptyIdx].quantity,
        meta_data: option.meta_data,
      });
      clearErrors(`selectedProducts.${emptyIdx}.quantity`);
    } else {
      append(productData);
    }

    setValue('productSearch', null, { shouldValidate: true });
  };


  const applyRowProduct = async (option, index) => {
    const rows = getValues('selectedProducts') || [];
    const current = rows[index] || {};

    // If product cleared, also clear quantity & any existing error
    if (!option) {
      update(index, {
        ...current,
        value: null,
        label: null,
        quantityLeft: null,
        quantity: '',
        meta_data: null,
      });
      clearErrors(`selectedProducts.${index}.quantity`);
      return;
    }

    // Product selected: keep quantity (default to 1) and sync related fields
    const updatedRow = {
      ...current,
      value: option.value ?? null,
      label: option.label ?? null,
      quantityLeft: option.quantityLeft ?? null,
      unitOfMeasure: option.unitOfMeasure ?? '',
      quantity: current.quantity === '' || current.quantity == null ? 1 : current.quantity,
      meta_data: option.meta_data || null,
    };

    console.log('Using meta_data from row option:', option.meta_data);

    update(index, updatedRow);

    // Clear any lingering error now that a product exists and we have a default quantity
    clearErrors(`selectedProducts.${index}.quantity`);
  };

  const onSubmit = async (data) => {
    // console.log("data========",data)
    // return false;
    if (!data.selectedProducts.length) {
      LoginUtility.toastNotifications('Please add at least one product.', "Error", "error");
      return;
    }

    try {
      const formattedData = {
        from_store_id: data.fromStore.value,
        to_store_id: data.toStore.value,
        notes: data.notes,
        // products: data.selectedProducts
        //   .filter((el) => el.value) // only send rows with a selected product
        //   .map((el) => ({
        //     slack: el.value,
        //     name: el.label,
        //     quantity: Number(el.quantity) || 1,
        //     quantity_left: el.quantityLeft,
        //   })),
        products: data.selectedProducts
        .filter((el) => el.value && Number(el.quantityLeft) > 0)
        .map((el) => ({
          slack: el.value,
          name: el.label,
          quantity: Number(el.quantity) || 1,
          quantity_left: el.quantityLeft,
        })),
      };

      // Add requisition fields if available
      if (requisitionReference) {
        formattedData.stock_transfer_reference = requisitionReference;
      }
      // if (requisitionSlack) {
      //   formattedData.requisition_slack = requisitionSlack;
      // }
      if (requisitionId) {
        formattedData.requisition_id = requisitionId;
      }

      if (!formattedData.products.length) {
        toast.error('Please select at least one product.');
        return;
      }
      // console.log("formattedData====",formattedData); return;
      if (isEdit) {
        const response = await updateStockTransfer(slack, formattedData);
        if (response.success) {
          LoginUtility.toastNotifications(response.message, "Success", "success");
          history.push(`/${localStorage.getItem('i18nextLng') || 'en'}/stock/transfer/list`);
        }
      } else {
        formattedData.store_id = Number(localStorage.getItem('store_id'))
        const response = await createStockTransfer(formattedData);
        if (response.success) {
          LoginUtility.toastNotifications(response.message, "Success", "success");
          history.push(`/${localStorage.getItem('i18nextLng') || 'en'}/stock/transfer/list`);
        }
      }
    } catch (e) {
      console.error('Error saving stock transfer:', e);
    }
  };

  useEffect(() => { if (isEdit && slack) fetchStockTransfer(); }, [isEdit, slack]); // eslint-disable-line

  // Pre-fill form from requisition data if available
  useEffect(() => {
    if (!isEdit) {
      const requisitionData = localStorage.getItem('stockTransferFromRequisition');
      if (requisitionData) {
        try {
          const data = JSON.parse(requisitionData);

          // Set requisition reference and slack
          setRequisitionReference(data.stock_transfer_reference || '');
          setRequisitionSlack(data.requisition_slack || '');
          setRequisitionId(data.requisition_id || '');

          // Use store details from requisition data directly
          const selectedProducts = data.products.map(p => {
            const parts = [p.name];
            if (p.product_code) parts.push(`Code: ${p.product_code}`);
            if (p.quantity_left !== undefined && p.quantity_left !== null) {
              parts.push(`Qty: ${p.quantity_left}${p.unit_of_measure ? ` ${p.unit_of_measure}` : ''}`);
            }
            return {
              value: p.slack,
              label: parts.join(' | '),
              quantity: p.quantity,
              quantityLeft: p.quantity_left,
              unitOfMeasure: p.unit_of_measure || ''
            };
          });

          reset({
            fromStore: data.from_store_details ? {
              label: data.from_store_details.name,
              value: data.from_store_details.id,
              store_code: data.from_store_details.store_code,
            } : null,
            toStore: data.to_store_details ? {
              label: data.to_store_details.name,
              value: data.to_store_details.id,
              store_code: data.to_store_details.store_code,
            } : null,
            notes: data.notes,
            selectedProducts: selectedProducts,
            productSearch: null,
          });

          // Clear the localStorage after pre-filling
          localStorage.removeItem('stockTransferFromRequisition');
        } catch (error) {
          console.error('Error parsing requisition data:', error);
          localStorage.removeItem('stockTransferFromRequisition');
        }
      }
    }
  }, [isEdit, reset]); // eslint-disable-line

  // Helper function to get meta data value
  const getFromMetaData = (meta_data, key) => {
    if (!meta_data || !Array.isArray(meta_data)) return null;
    const metaItem = meta_data.find(item => item.meta_key === key);
    return metaItem ? metaItem.meta_value : null;
  };

  return (
    <Card className="supplier-form-card">
      <Card.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h5 style={{ margin: 0 }}>{isEdit ? 'Edit Stock Transfer' : 'Add Stock Transfer'}</h5>
          <div>
            <Link
              to={`/${localStorage.getItem('i18nextLng') || 'en'}/stock/transfer/list`}
              className="btn btn-secondary me-2"
            >
              <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 6 }} />
              Back
            </Link>
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
          {/* Stores row */}
          <Row>
            <Col md={6}>
              <Form.Group controlId="fromStore">
                <Form.Label>From Store</Form.Label>
                <FieldWithUnderError error={errors.fromStore?.message}>
                  <Controller
                    control={control}
                    name="fromStore"
                    render={({ field }) => (
                      <AsyncSelect
                        {...field}
                        isDisabled
                        cacheOptions
                        loadOptions={fetchStoreOptions}
                        placeholder="From store"
                        styles={selectStyles(false)}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      />
                    )}
                  />
                </FieldWithUnderError>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="toStore">
                <Form.Label>To Store</Form.Label>
                <Controller
                  control={control}
                  name="toStore"
                  rules={{ required: 'To Store is required' }}
                  render={({ field }) => {
                    const err = errors.toStore?.message;
                    return (
                      <FieldWithUnderError error={err}>
                        <AsyncSelect
                          {...field}
                          cacheOptions
                          loadOptions={debouncedFetchStoreOptions}
                          onChange={(opt) => field.onChange(opt)}
                          placeholder="Type Store Name"
                          isClearable
                          styles={selectStyles(!!err)}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        />
                      </FieldWithUnderError>
                    );
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Requisition Reference row - show only if from requisition */}
          {requisitionReference && (
            <Row className="mt-3">
              <Col md={6}>
                <Form.Group controlId="requisitionReference">
                  <Form.Label>Requisition Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={requisitionReference}
                    disabled
                    style={{ backgroundColor: '#e9ecef' }}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Top product adder */}
          <Row className="mt-4">
            <Col md={6}>
              <Form.Group controlId="productSearch">
                <Form.Label>Search and Add Products</Form.Label>
                <FieldWithUnderError error={errors.selectedProducts?.message}>
                  <Controller
                    control={control}
                    name="productSearch"
                    render={({ field }) => (
                      <AsyncSelect
                        {...field}
                        cacheOptions={false}
                        loadOptions={debouncedFetchProductOptions}
                        onChange={(opt) => handleProductPickedFromTop(opt)}
                        placeholder="Enter minimum 3 characters"
                        isClearable
                        value={null}
                        styles={selectStyles(false)}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      />
                    )}
                  />
                </FieldWithUnderError>
              </Form.Group>
            </Col>
          </Row>

          {/* Dynamic product rows */}
          {fields.length > 0 && (
            <div style={{ marginTop: 16 }}>
              {fields.map((el, i) => {
                // use *watched* row for live values after remove/append/update
                const row = watchedRows?.[i] || {};
                const productErr = errors.selectedProducts?.[i]?.value?.message;
                const qtyErr = errors.selectedProducts?.[i]?.quantity?.message;
                return (
                  <Row key={el.key} style={{ marginTop: 16 }}>
                    {/* Product select */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Product Name &amp; Description</Form.Label>
                        <FieldWithUnderError error={productErr}>
                          <Controller
                            control={control}
                            name={`selectedProducts.${i}.value`}
                            rules={{ required: 'Name is required' }}
                            render={({ field }) => (
                              <AsyncSelect
                                cacheOptions={false}
                                loadOptions={debouncedFetchProductOptions}
                                placeholder=""
                                isClearable
                                // 🔑 drive the displayed option from the *watched* row
                                value={row?.value && row?.label ? { value: row.value, label: row.label } : null}
                                onChange={(opt) => {
                                  field.onChange(opt?.value ?? null);
                                  applyRowProduct(opt, i);
                                }}
                                // lock the row after a product is chosen (based on *watched* row)
                                isDisabled={!!row?.value}
                                styles={selectStyles(!!productErr)}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                              />
                            )}
                          />
                        </FieldWithUnderError>
                      </Form.Group>

                      {/* Meta Information Display - Only show for non-restaurant stores and when product is selected */}
                      {LoginUtility.isNotRestaurantStore(currentStore) && row?.value && row?.meta_data && (
                        <div style={{ 
                          marginTop: '8px',
                          backgroundColor: '#f8f9fa', 
                          padding: '8px 12px', 
                          borderRadius: '4px', 
                          border: '1px solid #dee2e6',
                          fontSize: '12px',
                          color: '#495057'
                        }}>
                          <strong>Meta Information:</strong>
                          {row.meta_data.map((meta, metaIndex) => {
                            const displayLabels = {
                              'generic_name': 'Generic',
                              'hsn_code': 'HSN',
                              'drug_schedule': 'Schedule',
                              'mfg_date': 'Mfg',
                              'exp_date': 'Exp',
                              'uom': 'UOM',
                              'mrp': 'MRP',
                              'manufacturer_name': 'Mfr',
                              'requires_prescription': 'Rx Required'
                            };
                            
                            const label = displayLabels[meta.meta_key] || meta.meta_key;
                            const value = meta.meta_key === 'mrp' ? `₹${meta.meta_value}` : 
                                          meta.meta_key === 'requires_prescription' && meta.meta_value === 'true' ? 'Yes' :
                                          meta.meta_key === 'requires_prescription' && meta.meta_value === 'false' ? 'No' :
                                          meta.meta_value;
                            
                            return (
                              <span key={metaIndex} style={{ marginLeft: '8px' }}>
                                {label}: <span style={{ 
                                  color: meta.meta_key === 'requires_prescription' && meta.meta_value === 'true' ? '#dc3545' : 'inherit',
                                  fontWeight: meta.meta_key === 'requires_prescription' && meta.meta_value === 'true' ? 'bold' : 'normal'
                                }}>{value}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </Col>

                    {/* Quantity */}
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Quantity</Form.Label>
                        <FieldWithUnderError error={qtyErr}>
                          <Controller
                            control={control}
                            name={`selectedProducts.${i}.quantity`}
                            rules={{
                              validate: () => {
                                const currentRow = getValues(`selectedProducts.${i}`);
                                // Skip validation if no product is chosen
                                if (!currentRow?.value) return true;

                                if (currentRow.quantity === '' || currentRow.quantity == null) {
                                  return 'Quantity is required';
                                }

                                const n = typeof currentRow.quantity === 'number'
                                  ? currentRow.quantity
                                  : parseFloat(currentRow.quantity);

                                if (!Number.isFinite(n)) {
                                  return 'The Quantity field must be numeric and may contain decimal points';
                                }
                                if (n <= 0) {
                                  return 'Enter a number > 0';
                                }
                                
                                // Check if quantity exceeds total stock left
                                const totalStockLeft = Number(currentRow.quantityLeft) || 0;
                                if (n > totalStockLeft) {
                                  return `Quantity must not exceed (${totalStockLeft})`;
                                }
                                
                                return true;
                              },
                              required: 'The Quantity field must be numeric and may contain decimal points'
                            }}
                            render={({ field }) => (
                              <input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0.01"
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const { value } = e.target;
                                  const parsed = value === '' ? '' : Number(value);
                                  field.onChange(Number.isFinite(parsed) ? parsed : value);
                                }}
                                // disabled={!!requisitionReference}
                                style={{
                                  width: '100%',
                                  height: FIELD_HEIGHT,
                                  borderRadius: 6,
                                  border: `1px solid ${qtyErr ? '#dc3545' : '#ced4da'}`,
                                  padding: '6px 12px',
                                  outline: 'none',
                                  boxSizing: 'border-box',
                                  boxShadow: qtyErr ? '0 0 0 .25rem rgba(220,53,69,.25)' : 'none',
                                  // backgroundColor: requisitionReference ? '#e9ecef' : 'white',
                                }}
                              />
                            )}
                          />
                        </FieldWithUnderError>
                      </Form.Group>
                    </Col>

                    {/* Remove button */}
                    {!requisitionReference && (
                      <Col md="auto" style={{ display: 'flex', alignItems: 'start' }}>
                        <div style={{ height: FIELD_HEIGHT + ERROR_HEIGHT + GAP_Y }}>
                          <Button
                            variant="outline-danger"
                            onClick={() => remove(i)}
                            title="Remove product"
                            style={{
                              height: FIELD_HEIGHT,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: 32, // roughly aligns with labels
                            }}
                          >
                            <FontAwesomeIcon icon={faClose} />
                          </Button>
                        </div>
                      </Col>
                    )}

                    {/* Stock left (use watched row) */}
                    <Col md={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                      <div
                        style={{
                          height: FIELD_HEIGHT + ERROR_HEIGHT + GAP_Y,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {Number(row?.quantityLeft) != 0 ?
                          <p style={{ margin: 0 }}>Total Stock Left: {Number(row?.quantityLeft) || 0}{row?.unitOfMeasure ? ` ${row.unitOfMeasure}` : ''}</p> : null}
                        {row?.value && row?.label && Number(row?.quantityLeft) == 0 ?
                          <p style={{ margin: 0 }}>Product stock is 0. Transfer not possible.</p> : null}
                      </div>
                    </Col>

                    {/* keep quantityLeft synced in form state */}
                    <Controller
                      control={control}
                      name={`selectedProducts.${i}.quantityLeft`}
                      render={({ field }) => <input type="hidden" {...field} />}
                    />
                  </Row>
                );
              })}
            </div>
          )}

          {/* Add Product button */}
          {!requisitionReference && (
            <div style={{ marginTop: 12 }}>
              <Button
                size="sm"
                variant="outline-primary"
                type="button"
                onClick={() =>
                  append({ value: null, label: null, quantity: '', quantityLeft: null })
                }
              >
                Add More
              </Button>
            </div>
          )}

          {/* Notes */}
          <Row className="mt-4">
            <Col md={6}>
              <Form.Group controlId="notes">
                <Form.Label>Notes</Form.Label>
                <FieldWithUnderError error={errors.notes?.message}>
                  <Controller
                    control={control}
                    name="notes"
                    render={({ field }) => (
                      <Form.Control as="textarea" rows={5} placeholder="Enter notes" {...field} />
                    )}
                  />
                </FieldWithUnderError>
              </Form.Group>
            </Col>
          </Row>

          <div style={{ marginTop: 16 }}>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                    style={{ marginRight: 6 }}
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} style={{ marginRight: 6 }} />
                  Save
                </>
              )}
            </Button>
            <Button variant="secondary" type="button" onClick={() => history.goBack()} style={{ marginLeft: 8 }}>
              <FontAwesomeIcon icon={faTimes} style={{ marginRight: 6 }} />
              Cancel
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

// export default StockTransferForm;

const mapStateToProps = (state) => ({
  currentStore: state.StoreReducer.currentStore,
});

export default connect(mapStateToProps)(StockTransferForm);
